import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { projects } from '@/data/projects';
import { getLenis } from '@/lib/lenisInstance';
import { HIJACK } from '@/lib/motionTokens';
import {
  CORRIDOR_LENGTH_VH_DESKTOP,
  SCROLL_LENGTH_VH_DESKTOP,
} from '@/lib/journeyPacing';

// Stations on the journey rail: intro + N projects + end portal. Derived from
// data so the layout scales with data/projects.ts — matches cameraPath /
// CameraRig / ScrollProgress.
export const JOURNEY_STATION_COUNT = projects.length + 2;

// Same `corridorShare` the hijack's snap targets use in `CameraRig.tsx` —
// the trailing slice (1 - corridorShare) of pin scroll is reserved for
// the EndPortal gallery rotation, not corridor scrub. Multiplying the
// sidebar's targetProgress by this scale so the click lands on the same
// snap target a wheel gesture would resolve to. Without it, sidebar
// clicks overshoot the hijack snap by a few vh per project and the
// exhibit's slide-in animation reads in a different state depending on
// which navigation method the user used.
const CORRIDOR_SHARE_DESKTOP =
  CORRIDOR_LENGTH_VH_DESKTOP / SCROLL_LENGTH_VH_DESKTOP;

/**
 * The journey's pinned ScrollTrigger — the one whose pin element is the
 * `.journey-container`. Matched specifically because HandoffPin adds a second
 * pinned trigger and a bare `pin instanceof HTMLElement` would pick the wrong
 * one. Returns null until the rig has created its pin.
 */
export function getJourneyPinTrigger(): ScrollTrigger | null {
  return (
    ScrollTrigger.getAll().find(
      (st) =>
        st.pin instanceof HTMLElement &&
        st.pin.classList.contains('journey-container'),
    ) ?? null
  );
}

/**
 * Scroll the page to a journey station through Lenis, snapping the camera
 * exactly where the hijack would resolve a wheel gesture to the same
 * station. Used by the sidebar chips. No-op if the pin trigger hasn't
 * been created yet.
 *
 * Inputs scale with `projects.length`:
 *   • JOURNEY_STATION_COUNT comes from `projects.length + 2`.
 *   • `trigger.end - trigger.start` is the live pin length (CameraRig's
 *     `end:` callback against `SCROLL_LENGTH_VH_DESKTOP`).
 *   • CORRIDOR_SHARE_DESKTOP is the same scale CameraRig applies to its
 *     `STATION_SNAP_TARGETS` array — sidebar and hijack land in lockstep.
 *
 * Previously the sidebar used a raw `index / (N-1)` progress without the
 * corridor share, which sent the click target ~6% past where the wheel
 * snap would land. The exhibit's distance-driven slide-in animation
 * read those two landing positions as different states; the user got
 * a slightly different framing depending on which navigation method
 * they used. Aligning here removes that inconsistency.
 */
export function scrollToJourneyStation(index: number): void {
  const trigger = getJourneyPinTrigger();
  if (!trigger) return;
  const p =
    (index / (JOURNEY_STATION_COUNT - 1)) * CORRIDOR_SHARE_DESKTOP;
  const targetY = trigger.start + p * (trigger.end - trigger.start);
  const lenis = getLenis();
  if (lenis) {
    lenis.scrollTo(targetY, { duration: HIJACK.sidebar.travelSeconds, lock: true });
  } else {
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }
}
